import { GridLegacy as Grid, Typography, Box, Slider } from '@mui/material'
import { C1Button, C1LoadingButton } from 'c1ui'
import { C1Dialog, C1DialogContent, C1DialogTitle, C1DialogActions } from 'components/core/dialog/C1Dialog'
import { Dropzone } from 'components/core/dropzone'
import { APIError } from 'components/core/errors/api'
import { useAlert } from 'hooks/useAlert'
import { useImageProcessor, uploadToCreateReturn } from 'hooks/useImageProcessor'
import { UserContext } from 'lib/context/app'
import { CropArea, cropToPng } from 'lib/crop-image'
import { useSimpleModal } from 'lib/modals'
import { SetUserAvatarRequest, SetUserAvatarSelfRequest } from 'pbts/c1/api/user/v1/user'
import { FC, useCallback, useEffect, useContext } from 'react'
import Cropper from 'react-easy-crop'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { SerializedErrorType } from 'redux_toolkit/lib/reducer'
import { setUserAvatarApi, setUserAvatarSelfApi } from 'redux_toolkit/user-avatar'

// Custom hook for form submission
const useUserAvatarSubmission = (userId: string | undefined, currentUserId: string | undefined): {
    submitAvatar: (file: File, croppedArea: CropArea) => Promise<boolean>
    inProgress: boolean
    apiError: SerializedErrorType | null
} => {
    const intl = useIntl()
    const toast = useAlert()
    const {
        mutate: setUserAvatar,
        inProgress: setUserAvatarInProgress,
        error: setUserAvatarError,
    } = setUserAvatarApi()
    const {
        mutate: setUserAvatarSelf,
        inProgress: setUserAvatarSelfInProgress,
        error: setUserAvatarSelfError,
    } = setUserAvatarSelfApi()

    // Determine which endpoint to use based on whether we're editing own profile
    const isOwnProfile = !userId || userId === currentUserId
    const inProgress = isOwnProfile ? setUserAvatarSelfInProgress : setUserAvatarInProgress
    const apiError = isOwnProfile ? setUserAvatarSelfError : setUserAvatarError

    const submitAvatar = useCallback(async (file: File, croppedArea: CropArea): Promise<boolean> => {
        try {
            const imageBlob = await cropToPng(file, croppedArea)
            const arrayBuffer = await imageBlob.arrayBuffer()
            const bytes = new Uint8Array(arrayBuffer)

            if (isOwnProfile) {
                // Use SetUserAvatarSelf for own profile
                const request = SetUserAvatarSelfRequest.create({
                    data: bytes,
                })
                await setUserAvatarSelf(request)
            } else {
                // Use SetUserAvatar for admin editing another user
                if (!userId) {
                    toast(intl.formatMessage({
                        id: 'user_avatar_upload_no_user_id_error',
                        defaultMessage: 'User ID is required',
                    }), 'error')
                    return false
                }
                const request = SetUserAvatarRequest.create({
                    userId,
                    data: bytes,
                })
                await setUserAvatar(request)
            }

            toast(intl.formatMessage({
                id: 'user_avatar_upload_success',
                defaultMessage: 'Avatar updated successfully',
            }), 'success')

            return true
        } catch (error) {
            console.error('Failed to save user avatar:', error)
            return false
        }
    }, [isOwnProfile, userId, setUserAvatar, setUserAvatarSelf, toast, intl])

    return {
        submitAvatar,
        inProgress,
        apiError: apiError || null,
    }
}

export const UserAvatarUploadDialog: FC<{
    userId?: string,
    close: () => void,
}> = ({ userId, close }) => {
    const intl = useIntl()
    const alert = useAlert()
    const { user: currentUser } = useContext(UserContext)

    // Custom hooks for separation of concerns
    const imageProcessor = useImageProcessor({
        errorMessageIds: {
            fileSize: 'user_avatar_upload_file_size_error',
            fileType: 'user_avatar_upload_file_type_error',
        },
    })
    const { submitAvatar, inProgress, apiError } = useUserAvatarSubmission(userId, currentUser?.id)

    const handleClose = useCallback((): void => {
        if (inProgress) return // Prevent closing during API call

        imageProcessor.reset()
        close()
    }, [inProgress, imageProcessor, close])

    const handleSave = useCallback(async (): Promise<void> => {
        if (!imageProcessor.uploadedFile || !imageProcessor.croppedArea) {
            return
        }

        const success = await submitAvatar(imageProcessor.uploadedFile, imageProcessor.croppedArea)
        if (success) {
            handleClose()
        }
    }, [imageProcessor.uploadedFile, imageProcessor.croppedArea, submitAvatar, handleClose])

    const canSave = imageProcessor.uploadedFile !== null &&
        imageProcessor.upload.status === 'ok' &&
        imageProcessor.croppedArea !== null &&
        !inProgress

    // Handle API error
    useEffect(() => {
        if (apiError) {
            alert(
                intl.formatMessage({
                    id: 'user_avatar_upload_error',
                    defaultMessage: 'Failed to update avatar: {error}',
                }, {
                    error: apiError.message || 'Unknown error',
                }),
                'error',
            )
        }
    }, [apiError, intl, alert])

    if (!userId) return null

    return (
        <C1Dialog
            open
            onClose={handleClose}
            maxWidth="md"
            aria-labelledby="user-avatar-update-title"
            aria-describedby="user-avatar-update-description"
        >
            <C1DialogTitle>
                <Typography id="user-avatar-update-title" variant="h6" component="h2">
                    <FormattedMessage
                        id="user_avatar_update_title"
                        defaultMessage="Update avatar"
                    />
                </Typography>
            </C1DialogTitle>
            <C1DialogContent>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography id="user-avatar-update-description">
                            <FormattedMessage
                                id="user_avatar_update_description"
                                defaultMessage="Upload and crop an image to use as the avatar. The image will be automatically converted to a square format and displayed in circular form."
                            />
                        </Typography>
                    </Grid>

                    {imageProcessor.upload.status === 'error' && (
                        <Grid item xs={12}>
                            <APIError
                                err={imageProcessor.upload.error}
                                title={
                                    <FormattedMessage
                                        id="user_avatar_file_validation_error"
                                        defaultMessage="Invalid file format or size"
                                    />
                                }
                            />
                        </Grid>
                    )}

                    {apiError && (
                        <Grid item xs={12}>
                            <APIError
                                err={apiError}
                                title={
                                    <FormattedMessage
                                        id="user_avatar_upload_error"
                                        defaultMessage="Failed to update avatar"
                                    />
                                }
                            />
                        </Grid>
                    )}

                    {!imageProcessor.uploadedFile && (
                        <Grid item xs={12}>
                            <Dropzone
                                onDrop={imageProcessor.handleFileDrop}
                                createReturn={uploadToCreateReturn(imageProcessor.upload)}
                            />
                        </Grid>
                    )}

                    {imageProcessor.uploadedFile && imageProcessor.imageUrl && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    <FormattedMessage
                                        id="user_avatar_crop_instructions"
                                        defaultMessage="Drag to move the crop area and use the slider to zoom. The avatar will be cropped to a square."
                                    />
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        height: 300,
                                        width: 300,
                                        backgroundColor: 'grey.100',
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Cropper
                                        image={imageProcessor.imageUrl}
                                        crop={imageProcessor.crop}
                                        zoom={imageProcessor.zoom}
                                        aspect={1}
                                        onCropChange={imageProcessor.setCrop}
                                        onZoomChange={imageProcessor.setZoom}
                                        onCropComplete={imageProcessor.onCropComplete}
                                        cropShape="round"
                                        style={{
                                            containerStyle: {
                                                width: '100%',
                                                height: '100%',
                                                backgroundColor: 'grey.100',
                                            },
                                        }}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    <FormattedMessage
                                        id="user_avatar_zoom_label"
                                        defaultMessage="Zoom"
                                    />
                                </Typography>
                                <Slider
                                    value={imageProcessor.zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    onChange={(_, value): void => imageProcessor.setZoom(value as number)}
                                    valueLabelDisplay="auto"
                                    aria-label={intl.formatMessage({
                                        id: 'user_avatar_zoom_slider_aria',
                                        defaultMessage: 'Image zoom control',
                                    })}
                                />
                            </Grid>

                            {imageProcessor.uploadedFile && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        <FormattedMessage
                                            id="user_avatar_file_info"
                                            defaultMessage="File: {file_name} ({file_size} KB)"
                                            values={{
                                                file_name: imageProcessor.uploadedFile.name,
                                                file_size: <FormattedNumber
                                                    value={imageProcessor.uploadedFile.size / 1024}
                                                    maximumFractionDigits={1}
                                                />,
                                            }}
                                        />
                                    </Typography>
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={inProgress}
                    aria-label={intl.formatMessage({
                        id: 'user_avatar_update_cancel_aria',
                        defaultMessage: 'Cancel avatar update',
                    })}
                >
                    <FormattedMessage
                        id="user_avatar_update_cancel"
                        defaultMessage="Cancel"
                    />
                </C1Button>
                <C1LoadingButton
                    variant="contained"
                    disabled={!canSave}
                    loading={inProgress}
                    onClick={handleSave}
                    aria-label={intl.formatMessage({
                        id: 'user_avatar_update_save_aria',
                        defaultMessage: 'Save avatar',
                    })}
                >
                    <FormattedMessage id="user_avatar_update_save" defaultMessage="Save" />
                </C1LoadingButton>
            </C1DialogActions>
        </C1Dialog>
    )
}

export const UserAvatarUpload: FC<{
    userId: string | undefined,
}> = ({ userId }): React.ReactElement => {
    const intl = useIntl()
    const { open } = useSimpleModal()

    return (
        <C1Button
            variant="outlined"
            onClick={open(UserAvatarUploadDialog, { userId })}
            disabled={!userId}
            aria-label={intl.formatMessage({
                id: 'user_avatar_update_button_aria',
                defaultMessage: 'Update avatar',
            })}
        >
            <FormattedMessage
                id="user_avatar_update_button"
                defaultMessage="Update avatar"
            />
        </C1Button>
    )
}
